// CSS
import './Directory.css';
// React hooks
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Global hooks
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config';
// Skybase components
import Sidebar from '../../components/Sidebar/Sidebar';
import DirectoryInstance from './DirectoryInstance/DirectoryInstance';
import DocumentPreviewModal from '../../components/Modals/FullScreenModal/DocumentPreviewModal/DocumentPreviewModal';
import FullScreenModal from '../../components/Modals/FullScreenModal/FullScreenModal';
import InformationalPopover from '../../components/Popovers/InformationalPopover/InformationalPopover';
import { Virtuoso } from 'react-virtuoso';                                                                                      // Virtualization
// External hooks/components
import { useAuth as useClerkAuth } from '@clerk/react';
// Media assets
import DirectoryIcon from '../../assets/EngineSelectorIcons/view-all-icon.png';
import BackButton from '../../assets/StandardIcons/ArrowIcons/chevron.png';


// =========
// Directory – represents the /directory Product page.
// =========
function Directory() {
    
    // destructure global context
    const { activeEngineJson, changeDirectoryNodeId, directoryChildrenId, folderNodeStack, popFolderNodeStack, pushFolderNodeStack } = useData();
    const { isSubscribed, checkIsSubscribed, activeUserJson } = useAuth();
    const { isLoading: isClerkLoading, getToken } = useClerkAuth();

    // init hooks
    const navigate = useNavigate('');

    
    // ================
    // onMount Behavior
    // ================
    // onMount --> check for valid Skybase Stripe subscription
    const [isInformationalPopoverOpen, setIsInformationalPopoverOpen] = useState(false);
    useEffect(() => {
        // checkSubscription()
        // ===================
        // Async. fn that prevents CurrentUsers without an active subscription from interacting with this page.
        const checkSubscription = async () => {
            // Don't perform a subscription-check if the Clerk User hasn't loaded yet.
            if (isClerkLoading) return;

            // Does CurrentUser have an active subscription?
            const subscribed = await checkIsSubscribed();

            // If NO: Display InformationalPopover.
            if (!subscribed) { setIsInformationalPopoverOpen(true); }
        };

        // Run the async. subscription-check
        checkSubscription();

        // Edge case: "bfcache === true" --> Forcibly reload the page to re-run the async. subscription-check
        const handlePageShow = (event) => {
            if (event.persisted) { window.location.reload(); }
        };
        window.addEventListener("pageshow", handlePageShow); // "pageShow" event listener

        // Edge case: No <Clerk JWT> --> Immediately redirect user from this page.
        checkForValidClerkTokenOtherwiseRedirect();

        // Remove listeners onDismount (prevent memory overflow)
        return () => { window.removeEventListener("pageshow", handlePageShow); };
    }, [isClerkLoading])

    
    // ==========================================
    // checkForValidClerkTokenOtherwiseRedirect()
    // ==========================================
    // Checks for a valid clerk token. Forcibly re-directs the user to '/login' if not.
    async function checkForValidClerkTokenOtherwiseRedirect() {
        let token = await getToken();
        if (!token) { navigate('../login') };
    }
    // onChange ('activeUserJson') --> immediately check again for valid subscription
    useEffect(() => {
        checkForValidClerkTokenOtherwiseRedirect();
    }, [activeUserJson]);


    // ==================
    // ANIMATION TRIGGERS
    // ==================
    useEffect(() => {
        // Select all elements with this class
        const elements = document.querySelectorAll('.SearchPage-fade-in');

        elements.forEach(el => {
            // Remove animation class
            el.classList.remove('SearchPage-fade-in');

            // Force reflow
            void el.offsetWidth;

            // Re-add animation class
            el.classList.add('SearchPage-fade-in');
        });
    }, [activeEngineJson]); // Re-run whenever items change

    // Manage the document preview behavior.
    const [isDocumentPreviewModalOpen, setIsDocumentPreviewModalOpen] = useState(false);
    const [targetDocument, setTargetDocument] = useState(null); // default none.

    // ==================
    // DIR INSTANCE CLICK
    // ==================
    const handleDirInstanceClick = (childId) => {
        // if folder
        if (childId.kind === 'folder') {
            // add the new node to the stack
            pushFolderNodeStack(childId.id);
            
            // traverse to new node
            changeDirectoryNodeId(childId.id);
        }

        // if document
        if (childId.kind === 'document') { 
            setTargetDocument(childId.id);      // update target doc
            setIsDocumentPreviewModalOpen(true) // open doc modal
        }
    }

    // ============================================
    // Track User Location in Folder Directory Tree
    // ============================================

    // Back Button onClick()
    const backButtonClick = () => { popFolderNodeStack(); }

    // ========================
    // FETCH THIS FOLDER's JSON
    // ========================
    const [currFolderJson, setCurrFolderJson] = useState(null);
    // onChange ('folderNodeStack') – get the active Folder's JSON
    useEffect(() => {
        fetchCurrFolderJson();
    }, [folderNodeStack]) // runs onMount
    async function fetchCurrFolderJson() {
        
        // Only fetch iff
        if (folderNodeStack.length !== 0) {

            // Edge case: Never fetch() if <Clerk JWT> DNE.
            const token = await getToken();
            if (!token) { return }; // don't fetch() if Clerk JWT unavailable


            // `GET /folders/{id}`
            const url = new URL(`${API_BASE_URL}/api/v1/folders/${encodeURIComponent(folderNodeStack.at(-1))}`);
            const response_object = await fetch(url,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            if (!response_object.ok) { throw new Error(`SKYBASE (client): HTTP error ${response_object.status} encountered when fetching folder ${folderNodeStack.at(-1)}`); };
            
            // extract JSON
            const folder_json = await response_object.json();

            // update JSON (triggers re-render)
            setCurrFolderJson(folder_json);
        } else {
            setCurrFolderJson(null);
        }
    };


    // =============
    // JSX COMPONENT
    // =============
    return(
        <div className='Layout'>
            <Sidebar/>
            <div className='Directory'>

                {/* Repeater Header */}
                <div className='Directory-repeater-header SearchPage-fade-in'>
                    {/* Type */}
                    <div
                        className='Directory-repeater-header-label--back-button'
                        onClick={() => backButtonClick()}>
                        <img
                            src={BackButton}
                            className={`Directory-repeater-header-label--back-button-icon ${folderNodeStack?.length === 0 ? 'inactive' : ''}`}
                            alt='Click to return to the previous Folder.'/>
                    </div>

                    {/* Name */}
                    <div className='Directory-repeater-header-label--name'>
                        <div>Name <span className='Directory-repeater-header--curr-folder-style'>{currFolderJson?.name}</span></div>
                        <div>Last Modified</div>
                    </div>
                </div>
                
                {/* Repeater */}
                <div className='Directory-repeater'>
                    
                    {/* Expected behavior: display Folders and Documents in current directory */}
                    { activeEngineJson &&
                        <Virtuoso
                            className='Directory-repeater-curr-dir'
                            data={directoryChildrenId}

                            itemContent={(index, childId) => (

                                <div
                                    key={childId.id}
                                    className='Directory-repeater-instance'
                                    style={{ "--stagger-index": Math.min(index, 0) }}
                                    onClick={() => handleDirInstanceClick(childId)}
                                >          
                                    <DirectoryInstance entity={childId}/>
                                </div>
                            )}
                            
                            // Artificially expands the viewport by 5000px top and bottom
                            increaseViewportBy={{ top: 5000, bottom: 5000 }}
                        />
                    }

                    {/* Edge cases */}
                    <div className='Directory-repeater-curr-dir'>

                        {/* Edge case: Empty directory */}
                        {activeEngineJson && directoryChildrenId?.length === 0 && <>
                            <span className='History-repeater--empty-text'>
                                <span className='SearchPage-fade-in'>This folder is empty.</span>
                            </span>
                        </>}

                        {/* Edge Case */}
                        {!activeEngineJson && <>
                            <span className='History-repeater--empty-text'>
                                <span className='SearchPage-fade-in'>Select a database to view its folder directory.</span>
                                <div className='History-repeater--empty-text-cta SearchPage-fade-in' onClick={() => navigate('../db')}>Select a database to search</div>
                            </span>
                        </>}

                    </div>
                </div>

                {/* DocumentPreviewModal */}
                <FullScreenModal isOpen={isDocumentPreviewModalOpen} onClose={() => setIsDocumentPreviewModalOpen(false)} children={<DocumentPreviewModal uuid={targetDocument} isReadOnly={true} triggerClose={() => setIsDocumentPreviewModalOpen(false)} />}/>
            </div>


            {/* InformationalPopover */}
            { isInformationalPopoverOpen &&
                <InformationalPopover
                    titleText={"Subscription Required"}
                    bodyText={"Please purchase a Skybase subscription plan to continue."}
                    primaryButtonText={"View Subscription Plans"}
                    primaryButtonCallback={() => navigate('../pricing')}
                    secondaryButtonText={"Return Home"}
                    secondaryButtonCallback={() => navigate('../')}
                />
            }
        </div>
    )
}


// Export statement
export default Directory;